import {
  type ComponentType,
  type HTMLAttributes,
  type MutableRefObject,
  type ReactNode,
  type Ref,
  createContext,
  forwardRef,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import { PatternFormat } from 'react-number-format';
import { HelpCircle, InfoCircle } from '@untitledui/icons';
import { Email, Link, Search, EyeOpen, EyeClosed } from '../../../icons';
import type {
  InputProps as AriaInputProps,
  TextFieldProps as AriaTextFieldProps
} from 'react-aria-components';
import {
  Group as AriaGroup,
  Input as AriaInput,
  TextField as AriaTextField
} from 'react-aria-components';
import { HintText } from './hint-text';
import { InputGroup } from './input-group';
import { Label } from './label';
import { Tooltip, TooltipTrigger } from '../tooltip/tooltip';
import { cx, sortCx } from '../../../utils/cx';
import { CountrySelectorAddon } from './country-selector-addon';
import { CurrencySelectorAddon } from './currency-selector-addon';

const defaultIcons: Record<
  string,
  ComponentType<HTMLAttributes<HTMLOrSVGElement>>
> = {
  email: Email,
  search: Search,
  url: Link
};

type InputSize = 'sm' | 'md';

type InputStyleProps = {
  size?: InputSize;
  wrapperClassName?: string;
  inputClassName?: string;
  iconClassName?: string;
  tooltipClassName?: string;
};

type PrefixVariant = 'default' | 'segmented';

export interface InputBaseProps extends TextFieldProps {
  /** Tooltip message on hover. */
  tooltip?: string;
  /**
   * Input size.
   * @default "sm"
   */
  size?: InputSize;
  /** Placeholder text. */
  placeholder?: string;
  /** Class name for the icon. */
  iconClassName?: string;
  /** Class name for the input. */
  inputClassName?: string;
  /** Class name for the input wrapper. */
  wrapperClassName?: string;
  /** Class name for the tooltip. */
  tooltipClassName?: string;
  /** Class name for the label. */
  labelClassName?: string;
  /** Keyboard shortcut to display. */
  shortcut?: string | boolean;
  groupRef?: Ref<HTMLDivElement>;
  /** Icon component to display on the left side of the input. */
  icon?: ComponentType<HTMLAttributes<HTMLOrSVGElement>>;
  /** Forces the input into a focused state for preview/testing. */
  isFocused?: boolean;
  /** Format string for react-number-format */
  format?: string;
  /** Mask character for react-number-format */
  mask?: string;
  /** Whether to allow empty formatting */
  allowEmptyFormatting?: boolean;
  /** Whether to hide the default icon associated with the input type. */
  hideIcon?: boolean;
  /** Element to display before the input. */
  prefix?: ReactNode;
  /** Visual style for the prefix element. */
  prefixVariant?: PrefixVariant;
  /** Element to display after the input. */
  suffix?: ReactNode;
  /** Whether to show password visibility toggle for password inputs. */
  showPasswordToggle?: boolean;
  /** Controlled state for password visibility (if you need external control). */
  isPasswordVisible?: boolean;
  /** Callback when password visibility changes. */
  onPasswordVisibilityChange?: (visible: boolean) => void;
}

export const InputBase = forwardRef<
  HTMLInputElement,
  Omit<InputBaseProps, 'label' | 'hint'>
>(
  (
    {
      tooltip,
      shortcut,
      groupRef,
      size = 'sm',
      isInvalid,
      isDisabled,
      icon: PropIcon,
      placeholder,
      wrapperClassName,
      tooltipClassName,
      inputClassName,
      iconClassName,
      isFocused,
      format,
      mask,
      allowEmptyFormatting,
      hideIcon,
      prefix,
      prefixVariant = 'default',
      onChange,
      suffix,
      showPasswordToggle,
      isPasswordVisible: controlledPasswordVisible,
      onPasswordVisibilityChange,
      // Omit this prop to avoid invalid HTML attribute warning
      isRequired: _isRequired,
      ...inputProps
    },
    ref
  ) => {
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [internalPasswordVisible, setInternalPasswordVisible] =
      useState(false);

    // Use controlled state if provided, otherwise use internal state
    const isPasswordVisible =
      controlledPasswordVisible ?? internalPasswordVisible;

    const togglePasswordVisibility = () => {
      const newValue = !isPasswordVisible;
      setInternalPasswordVisible(newValue);
      onPasswordVisibilityChange?.(newValue);
    };

    // Determine if this is a password input that should show the toggle
    const isPasswordInput = inputProps.type === 'password';
    const shouldShowPasswordToggle =
      isPasswordInput && showPasswordToggle !== false;

    // Compute the actual input type based on password visibility
    const computedType =
      isPasswordInput && isPasswordVisible ? 'text' : inputProps.type;

    const DefaultIcon = inputProps.type
      ? defaultIcons[inputProps.type]
      : undefined;
    const Icon = PropIcon || (!hideIcon ? DefaultIcon : undefined);

    // If the input is inside a `TextFieldContext`, use its context to simplify applying styles
    const context = useContext(TextFieldContext);

    const inputSize = context?.size || size;

    const sizes = sortCx({
      sm: {
        root: 'px-3 py-2 gap-2',
        icon: 'size-5',
        shortcut: 'pr-2.5',
        segmentedPrefix: '-my-2 -ml-3 mr-2 px-3'
      },
      md: {
        root: 'px-3.5 py-2.5 gap-2',
        icon: 'size-5',
        shortcut: 'pr-3',
        segmentedPrefix: '-my-2.5 -ml-3.5 mr-2.5 px-3.5'
      }
    });

    const setInputRef = (node: HTMLInputElement | null) => {
      inputRef.current = node;
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        (ref as MutableRefObject<HTMLInputElement | null>).current = node;
      }
    };

    const shouldForceFocus = Boolean(isFocused && !isDisabled);

    useEffect(() => {
      if (typeof window === 'undefined') {
        return;
      }

      const node = inputRef.current;
      if (!node) {
        return;
      }

      if (isDisabled) {
        if (document.activeElement === node) {
          node.blur();
        }
        return;
      }

      if (shouldForceFocus) {
        requestAnimationFrame(() => node.focus());
      } else if (!shouldForceFocus && document.activeElement === node) {
        node.blur();
      }
    }, [shouldForceFocus, isDisabled]);

    return (
      <AriaGroup
        {...{ isDisabled, isInvalid }}
        ref={groupRef}
        className={({ isFocusWithin, isDisabled, isInvalid }) =>
          cx(
            'relative flex w-full flex-row items-center rounded-lg bg-primary shadow-xs border border-[var(--color-border-primary)] transition-shadow duration-100 ease-linear',

            isFocusWithin &&
              !isDisabled &&
              'ring-2 ring-[var(--color-focus-ring)] ring-inset',

            // Disabled state styles
            isDisabled &&
              'cursor-not-allowed bg-disabled_subtle ring-[var(--color-border-disabled_subtle)]',
            'group-disabled:cursor-not-allowed group-disabled:bg-disabled_subtle group-disabled:ring-[var(--color-border-disabled_subtle)]',

            // Invalid state styles
            isInvalid && 'ring-[var(--color-error-200)]',
            'group-invalid:ring-[var(--color-error-200)]',

            // Invalid state with focus-within styles
            isInvalid &&
              isFocusWithin &&
              'ring-2 ring-[var(--color-focus-ring-error)]',
            isFocusWithin &&
              'group-invalid:ring-2 group-invalid:ring-[var(--color-focus-ring-error)]',
            context?.wrapperClassName,
            wrapperClassName,
            sizes[inputSize].root
          )
        }
      >
        {/* Leading icon and Payment icon */}
        {Icon && (
          <Icon
            className={cx(
              'shrink-0 text-fg-quaternary',
              isDisabled && 'text-fg-disabled',
              sizes[inputSize].icon,
              context?.iconClassName,
              iconClassName
            )}
          />
        )}

        {/* Prefix */}
        {prefix && (
          <span
            className={cx(
              'text-md text-tertiary',
              prefixVariant === 'segmented' &&
                cx(
                  'self-stretch rounded-l-[inherit] border-r border-[var(--color-border-primary)] bg-secondary flex items-center',
                  sizes[inputSize].segmentedPrefix
                ),
              isDisabled && 'text-disabled'
            )}
          >
            {prefix}
          </span>
        )}

        {/* Input field */}
        {format ? (
          <PatternFormat
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            {...(inputProps as any)}
            disabled={isDisabled}
            format={format}
            mask={mask}
            allowEmptyFormatting={allowEmptyFormatting}
            customInput={AriaInput}
            getInputRef={setInputRef}
            placeholder={placeholder}
            onChange={(e) => {
              if (onChange) {
                onChange(e.target.value);
              }
            }}
            className={cx(
              'm-0 min-w-0 flex-1 bg-transparent text-md text-primary ring-0 outline-hidden placeholder:text-placeholder autofill:rounded-lg autofill:text-primary',
              isDisabled && 'cursor-not-allowed text-disabled',
              context?.inputClassName,
              inputClassName
            )}
          />
        ) : (
          <AriaInput
            {...(inputProps as AriaInputProps)}
            type={computedType}
            ref={setInputRef}
            placeholder={placeholder}
            disabled={isDisabled}
            onChange={(e) => {
              if (onChange) {
                onChange(e.target.value);
              }
            }}
            className={cx(
              'm-0 min-w-0 flex-1 bg-transparent text-md text-primary ring-0 outline-hidden placeholder:text-placeholder autofill:rounded-lg autofill:text-primary',
              isDisabled && 'cursor-not-allowed text-disabled',
              context?.inputClassName,
              inputClassName
            )}
          />
        )}

        {/* Password visibility toggle */}
        {shouldShowPasswordToggle && (
          <button
            type="button"
            onClick={togglePasswordVisibility}
            disabled={isDisabled}
            tabIndex={-1}
            className={cx(
              'flex shrink-0 items-center justify-center text-fg-quaternary transition duration-200 hover:text-fg-quaternary_hover focus:outline-none',
              isDisabled && 'cursor-not-allowed text-fg-disabled'
            )}
            aria-label={isPasswordVisible ? 'Hide password' : 'Show password'}
          >
            {isPasswordVisible ? (
              <EyeOpen className="size-5" />
            ) : (
              <EyeClosed className="size-5" />
            )}
          </button>
        )}

        {/* Suffix */}
        {suffix && (
          <span
            className={cx(
              'text-md text-tertiary',
              isDisabled && 'text-disabled'
            )}
          >
            {suffix}
          </span>
        )}

        {/* Tooltip and help icon */}
        {tooltip && !isInvalid && (
          <Tooltip title={tooltip} placement="top">
            <TooltipTrigger
              slot={null}
              className={cx(
                'cursor-pointer text-fg-quaternary transition duration-200 hover:text-fg-quaternary_hover focus:text-fg-quaternary_hover',
                context?.tooltipClassName,
                tooltipClassName
              )}
            >
              <HelpCircle className="size-4" />
            </TooltipTrigger>
          </Tooltip>
        )}

        {/* Invalid icon */}
        {isInvalid && (
          <InfoCircle
            className={cx(
              'pointer-events-none size-4 text-fg-error-secondary',
              context?.tooltipClassName,
              tooltipClassName
            )}
          />
        )}

        {/* Shortcut */}
        {shortcut && (
          <div
            className={cx(
              'pointer-events-none absolute inset-y-0.5 right-0.5 z-10 flex items-center rounded-r-[inherit] bg-linear-to-r from-transparent to-bg-primary to-40% pl-8',
              sizes[inputSize].shortcut
            )}
          >
            <span
              className={cx(
                'pointer-events-none rounded px-1 py-px text-xs font-medium text-quaternary ring-1 ring-secondary select-none ring-inset',
                isDisabled && 'bg-transparent text-disabled'
              )}
              aria-hidden="true"
            >
              {typeof shortcut === 'string' ? shortcut : '⌘K'}
            </span>
          </div>
        )}
      </AriaGroup>
    );
  }
);

InputBase.displayName = 'InputBase';

interface BaseProps {
  /** Label text for the input */
  label?: string;
  /** Helper text displayed below the input */
  hint?: ReactNode;
}

interface TextFieldProps
  extends BaseProps,
    AriaTextFieldProps,
    InputStyleProps {
  ref?: Ref<HTMLDivElement>;
}

const TextFieldContext = createContext<TextFieldProps>({});

export const TextField = forwardRef<HTMLDivElement, TextFieldProps>(
  ({ className, ...props }, ref) => {
    return (
      <TextFieldContext.Provider value={props}>
        <AriaTextField
          {...props}
          ref={ref}
          data-input-wrapper
          className={(state) =>
            cx(
              'group flex h-max w-full flex-col items-start justify-start gap-1.5',
              typeof className === 'function' ? className(state) : className
            )
          }
        />
      </TextFieldContext.Provider>
    );
  }
);

TextField.displayName = 'TextField';

export interface InputProps extends InputBaseProps {
  /** Whether to hide required indicator from label */
  hideRequiredIndicator?: boolean;
  /** The country code to use for the phone input. */
  country?: string;
  /** The currency code to use for the currency input. */
  currency?: string;
  /** Whether to hide the country selector for tel inputs. */
  hideCountrySelector?: boolean;
}

const countryFormats: Record<string, { format: string; placeholder: string }> =
  {
    US: { format: '+1 (###) ###-####', placeholder: '+1 (555) 000-0000' }
  };

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      size = 'sm',
      placeholder,
      icon: Icon,
      label,
      hint,
      shortcut,
      hideRequiredIndicator,
      className,
      groupRef,
      tooltip,
      iconClassName,
      inputClassName,
      labelClassName,
      wrapperClassName,
      tooltipClassName,
      isFocused,
      format,
      mask,
      allowEmptyFormatting,
      hideIcon,
      prefix,
      suffix,
      country = 'US',
      currency,
      hideCountrySelector,
      prefixVariant = 'default',
      ...props
    },
    ref
  ) => {
    if (props.type === 'tel') {
      const countrySettings =
        countryFormats[country] || countryFormats.US || countryFormats.US!;

      return (
        <InputGroup
          size={size}
          label={label}
          hint={hint}
          labelClassName={labelClassName}
          leadingAddon={
            hideCountrySelector ? undefined : (
              <CountrySelectorAddon countryCode={country} />
            )
          }
          className={className as string}
          {...props}
        >
          <InputBase
            {...{
              ref,
              groupRef,
              size,
              placeholder: placeholder || countrySettings.placeholder,
              icon: Icon,
              shortcut,
              iconClassName,
              inputClassName,
              wrapperClassName,
              tooltipClassName,
              tooltip,
              isFocused,
              format: format || countrySettings.format,
              mask: mask || '_',
              allowEmptyFormatting,
              hideIcon,
              prefix,
              prefixVariant,
              suffix,
              ...props
            }}
          />
        </InputGroup>
      );
    }

    if (currency) {
      return (
        <InputGroup
          size={size}
          label={label}
          hint={hint}
          labelClassName={labelClassName}
          trailingAddon={<CurrencySelectorAddon currencyCode={currency} />}
          className={className as string}
          {...props}
        >
          <InputBase
            {...{
              ref,
              groupRef,
              size,
              placeholder,
              icon: Icon,
              shortcut,
              iconClassName,
              inputClassName,
              wrapperClassName,
              tooltipClassName,
              tooltip,
              isFocused,
              format,
              mask,
              allowEmptyFormatting,
              hideIcon,
              prefix,
              prefixVariant,
              suffix,
              ...props
            }}
          />
        </InputGroup>
      );
    }

    return (
      <TextField
        aria-label={!label ? placeholder : undefined}
        {...props}
        className={className}
      >
        {({ isRequired, isInvalid }) => (
          <>
            {label && (
              <Label
                isRequired={
                  hideRequiredIndicator ? !hideRequiredIndicator : isRequired
                }
                className={labelClassName}
              >
                {label}
              </Label>
            )}

            <InputBase
              {...{
                ref,
                groupRef,
                size,
                placeholder,
                icon: Icon,
                shortcut,
                iconClassName,
                inputClassName,
                wrapperClassName,
                tooltipClassName,
                tooltip,
                isFocused,
                format,
                mask,
                allowEmptyFormatting,
                hideIcon,
                prefix,
                prefixVariant,
                suffix,
                ...props
              }}
            />

            {hint && <HintText isInvalid={isInvalid}>{hint}</HintText>}
          </>
        )}
      </TextField>
    );
  }
);

Input.displayName = 'Input';
